import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import * as React from 'react';
import { Label } from '../../../components-v2/Label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components-v2/Select';
import { THEME } from '../../../components-v2/Theme/theme';
import { HelpTooltip } from '../../HelpTooltip';
import { RadioButton } from '../../RadioButton';
import { StarRating } from '../../surveys/StarRating';
import {
	AUTO_REPLY_CAUTION_LEVELS,
	AUTO_REPLY_MODE_OPTIONS,
	DEFAULT_MINIMUM_CONFIDENCE,
	DEFAULT_MINIMUM_STAR_RATING,
	STAR_RATING_LIMIT,
	STAR_THRESHOLD_OPTIONS,
} from './constants';
import { styleSheet } from './styles';

interface IProps {
	settings?: Api.IGoogleReviewAutoReplySettings;
	onChange: (settings: Api.IGoogleReviewAutoReplySettings) => void;
}

export const AutoReplyModeOptions: React.FC<IProps> = ({ settings, onChange }) => {
	const mode = settings?.mode ?? Api.GoogleReviewAutoReplyMode.Off;

	const onModeChange = (nextMode: Api.GoogleReviewAutoReplyMode) => {
		// Thresholds are kept across mode switches so a round-trip back restores the user's previous
		// selections; the API ignores whichever does not apply to the saved mode.
		onChange({
			mode: nextMode,
			minimumStarRating:
				settings?.minimumStarRating ??
				(nextMode === Api.GoogleReviewAutoReplyMode.StarThreshold ? DEFAULT_MINIMUM_STAR_RATING : undefined),
			minimumConfidence:
				settings?.minimumConfidence ??
				(nextMode === Api.GoogleReviewAutoReplyMode.AIDiscretion ? DEFAULT_MINIMUM_CONFIDENCE : undefined),
		});
	};

	return (
		<div className={css(styleSheet.group)}>
			{AUTO_REPLY_MODE_OPTIONS.map(option => (
				<React.Fragment key={option.mode}>
					<RadioButton
						checked={mode === option.mode}
						id={`review-auto-reply-mode-${option.mode}`}
						name='review-auto-reply-mode'
						onChange={() => onModeChange(option.mode)}
						type='large'
					>
						<span>{option.label}</span>
					</RadioButton>
					{option.mode === Api.GoogleReviewAutoReplyMode.StarThreshold &&
					mode === Api.GoogleReviewAutoReplyMode.StarThreshold ? (
						<div className={css(styleSheet.autoReplySubSetting)}>
							<Select
								items={STAR_THRESHOLD_OPTIONS}
								value={String(settings?.minimumStarRating ?? DEFAULT_MINIMUM_STAR_RATING)}
								onValueChange={value =>
									onChange({
										...settings,
										mode: Api.GoogleReviewAutoReplyMode.StarThreshold,
										minimumStarRating: Number(value),
									})
								}
							>
								<SelectTrigger>
									<SelectValue placeholder='Select a rating...' />
								</SelectTrigger>
								<SelectContent>
									{STAR_THRESHOLD_OPTIONS.map(starOption => (
										<SelectItem key={starOption.value} value={starOption.value}>
											<div className={css(styleSheet.starRating)}>
												<span>{starOption.label}</span>
												<StarRating
													fillColor={THEME.colors.pill.yellow.icon}
													readonly
													total={STAR_RATING_LIMIT}
													value={Number(starOption.value)}
												/>
											</div>
										</SelectItem>
									))}
								</SelectContent>
							</Select>
							<Label styles={[styleSheet.label]}>Anything lower waits for your approval.</Label>
						</div>
					) : null}
					{option.mode === Api.GoogleReviewAutoReplyMode.AIDiscretion &&
					mode === Api.GoogleReviewAutoReplyMode.AIDiscretion ? (
						<div className={css(styleSheet.autoReplySubSetting)}>
							<Label styles={[styleSheet.label]}>How cautious should AI be?</Label>
							{AUTO_REPLY_CAUTION_LEVELS.map(level => (
								<RadioButton
									className={css(styleSheet.cautionOption)}
									checked={(settings?.minimumConfidence ?? DEFAULT_MINIMUM_CONFIDENCE) === level.minimumConfidence}
									id={`review-auto-reply-caution-${level.minimumConfidence}`}
									key={level.minimumConfidence}
									name='review-auto-reply-caution-level'
									onChange={() =>
										onChange({
											...settings,
											mode: Api.GoogleReviewAutoReplyMode.AIDiscretion,
											minimumConfidence: level.minimumConfidence,
										})
									}
								>
									<span className={css(styleSheet.cautionText)}>
										{level.label}
										<HelpTooltip preferredPlacement={['right', 'top']} styles={[styleSheet.cautionHelpIcon]}>
											{level.description}
										</HelpTooltip>
									</span>
								</RadioButton>
							))}
						</div>
					) : null}
				</React.Fragment>
			))}
		</div>
	);
};
